<template>
  <div class="attendee-segment-raw-container">
    <!-- 페이지 헤더 영역 -->
    <div class="page-header">
      <div class="title-row">
        <h1 class="page-title">
          이벤트 참여 관리
        </h1>
        <div class="header-actions">
          <!-- 이벤트는 쿼리 파라미터에서 자동으로 가져옴 -->
        </div>
      </div>
      <span class="pageDesc">이벤트 참여자 현황을 실시간으로 확인하고 고객 세그먼트별 활동을 분석할 수 있습니다.</span>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="content-section">
      <!-- 참여자 관리 탭 -->
      <div class="content-wrap-card">
        <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
          <a-tab-pane key="monitoring" tab="참여 현황 모니터링">
            <ParticipantMonitoring
              :participants="filteredParticipants"
              :loading="loading"
              :event-list="mockEvents"
              :selected-event-id="selectedEventId"
              :hide-event-select="true"
              @filter-change="handleFilterChange"
            />
          </a-tab-pane>

          <a-tab-pane key="aarrr" tab="이벤트 인터랙션 분석">
            <AarrrMetricsAnalysis
              :event-data="eventData"
              :attendee-data="attendeeData"
              @refresh="fetchEventData"
            />
          </a-tab-pane>

          <a-tab-pane key="activity" tab="활동분석">
            <ActivityAnalysis
              :event-id="selectedEventId"
            />
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted, provide, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from '@/axios';
import ParticipantMonitoring from './participation/ParticipantMonitoring.vue';
import ActivityAnalysis from './participation/ActivityAnalysis.vue';
import AarrrMetricsAnalysis from './participation/AarrrMetricsAnalysis.vue';
import eventsApis from './api';

export default {
  name: 'AttendeeSegmentRaw',
  components: {
    ParticipantMonitoring,
    ActivityAnalysis,
    AarrrMetricsAnalysis
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const apis = eventsApis(axios, store);

    // 상태 관리 - 쿼리 파라미터에서 eventId와 tab을 가져오고, 없으면 기본값 사용
    const selectedEventId = ref(route.query.eventId || '68ca33f040d408b2a0a30924');
    // 쿼리 파라미터에서 tab을 가져오고, 없으면 'monitoring'을 기본값으로 설정
    const activeTab = ref(route.query.tab || 'monitoring');

    // 자식 컴포넌트에 selectedEventId 제공
    provide('selectedEventId', selectedEventId);
    const loading = ref(false);
    const currentFilter = ref('all');

    // AARRR 분석을 위한 데이터
    const eventData = ref(null);
    const attendeeData = ref(null);

    // 이벤트 목록은 ParticipantMonitoring 컴포넌트에서 API로 직접 로드
    const mockEvents = ref([]);

    // 임시 데이터 - 참여자 목록
    const participants = ref([
      {
        id: 'p001',
        name: '김철수',
        phone: '010-1234-5678',
        email: 'kim@example.com',
        hospital: '서울대학교병원',
        status: 'shared',
        sharedAt: '2024-01-15 14:23:00',
        couponReceived: false,
        loggedIn: false,
        lastActivity: '2024-01-15 14:23:00'
      },
      {
        id: 'p002',
        name: '이영희',
        phone: '010-2345-6789',
        email: 'lee@example.com',
        hospital: '연세대학교병원',
        status: 'coupon_received',
        sharedAt: '2024-01-14 10:15:00',
        couponReceived: true,
        loggedIn: false,
        couponReceivedAt: '2024-01-14 10:30:00',
        lastActivity: '2024-01-14 10:30:00'
      },
      {
        id: 'p003',
        name: '박민수',
        phone: '010-3456-7890',
        email: 'park@example.com',
        hospital: '삼성서울병원',
        status: 'logged_in',
        sharedAt: '2024-01-13 09:00:00',
        couponReceived: true,
        loggedIn: true,
        couponReceivedAt: '2024-01-13 09:15:00',
        loggedInAt: '2024-01-13 09:20:00',
        lastActivity: '2024-01-13 09:20:00'
      },
      {
        id: 'p004',
        name: '정수진',
        phone: '010-4567-8901',
        email: 'jung@example.com',
        hospital: '서울아산병원',
        status: 'participated',
        sharedAt: null,
        couponReceived: false,
        loggedIn: false,
        lastActivity: '2024-01-12 15:00:00'
      },
      // 더 많은 참여자 데이터 추가
      ...Array.from({ length: 50 }, (_, i) => ({
        id: `p${String(i + 5).padStart(3, '0')}`,
        name: `참여자${i + 5}`,
        phone: `010-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
        email: `user${i + 5}@example.com`,
        hospital: ['서울대학교병원', '연세대학교병원', '삼성서울병원', '서울아산병원'][Math.floor(Math.random() * 4)],
        status: ['participated', 'shared', 'coupon_received', 'logged_in'][Math.floor(Math.random() * 4)],
        sharedAt: Math.random() > 0.3 ? '2024-01-15 ' + String(Math.floor(Math.random() * 24)).padStart(2, '0') + ':' + String(Math.floor(Math.random() * 60)).padStart(2, '0') + ':00' : null,
        couponReceived: Math.random() > 0.5,
        loggedIn: Math.random() > 0.7,
        lastActivity: '2024-01-' + String(Math.floor(Math.random() * 15) + 1).padStart(2, '0') + ' ' + String(Math.floor(Math.random() * 24)).padStart(2, '0') + ':' + String(Math.floor(Math.random() * 60)).padStart(2, '0') + ':00'
      }))
    ]);

    // 필터링된 참여자
    const filteredParticipants = computed(() => {
      if (currentFilter.value === 'all') return participants.value;

      return participants.value.filter(p => {
        switch (currentFilter.value) {
          case 'no_share':
            return p.status === 'participated';
          case 'no_coupon':
            return p.status === 'shared';
          case 'no_login':
            return p.status === 'coupon_received';
          default:
            return true;
        }
      });
    });

    // API 데이터 로드 함수
    const fetchEventData = async () => {
      if (!selectedEventId.value) return;

      loading.value = true;
      try {
        // 이벤트 목록 조회 (AARRR 메트릭 포함)
        await apis.getEventsWithStatus(
          { status: 'ongoing', page: 1, limit: 100 },
          (response) => {
            if (response.data) {
              const events = response.data.items || response.data.data || response.data || [];
              const targetEvent = events.find(e => e._id === selectedEventId.value);
              if (targetEvent) {
                eventData.value = targetEvent;
                console.log('Event data loaded with AARRR metrics:', targetEvent.aarrrMetrics);
              }
            }
          },
          (error) => {
            console.error('Failed to load event data:', error);
          }
        );

        // 참여자 활동 데이터 조회
        await apis.getEventAttendees(
          selectedEventId.value,
          { page: 1, limit: 10, sort: 'participatedAt', order: 'desc' },
          (response) => {
            if (response.data?.success) {
              attendeeData.value = response.data.data;
              console.log('Attendee data loaded with activity types:', attendeeData.value.activityTypes);
            }
          },
          (error) => {
            console.error('Failed to load attendee data:', error);
          }
        );
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        loading.value = false;
      }
    };

    // 메서드
    const handleTabChange = (key) => {
      console.log('Tab changed:', key);

      // URL 쿼리 파라미터 업데이트
      router.push({
        query: {
          ...route.query,
          tab: key
        }
      });

      // AARRR 탭 선택 시 데이터 로드
      if (key === 'aarrr' && (!eventData.value || !attendeeData.value)) {
        fetchEventData();
      }
    };

    const handleFilterChange = (filter) => {
      currentFilter.value = filter;
    };

    // 라우트 쿼리 변경 감지
    watch(() => route.query.eventId, (newEventId) => {
      if (newEventId) {
        selectedEventId.value = newEventId;
        console.log('EventId changed from query:', newEventId);
      }
    });

    // tab 쿼리 파라미터 변경 감지
    watch(() => route.query.tab, (newTab) => {
      if (newTab && newTab !== activeTab.value) {
        activeTab.value = newTab;
        console.log('Tab changed from query:', newTab);

        // AARRR 탭 선택 시 데이터 로드
        if (newTab === 'aarrr' && (!eventData.value || !attendeeData.value)) {
          fetchEventData();
        }
      }
    });

    onMounted(() => {
      console.log('이벤트 참여 관리 페이지 로드됨');
      console.log('Event ID from query or default:', selectedEventId.value);
      console.log('Active tab from query or default:', activeTab.value);

      // AARRR 탭이 선택되어 있으면 데이터 로드
      if (activeTab.value === 'aarrr') {
        fetchEventData();
      }

      // URL에 tab 파라미터가 없으면 추가
      if (!route.query.tab) {
        router.replace({
          query: {
            ...route.query,
            tab: activeTab.value
          }
        });
      }
    });

    return {
      selectedEventId,
      activeTab,
      loading,
      mockEvents,
      participants,
      filteredParticipants,
      eventData,
      attendeeData,
      handleTabChange,
      handleFilterChange,
      fetchEventData
    };
  }
};
</script>

<style scoped>
.attendee-segment-raw-container {
  padding: 24px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: rgba(0, 0, 0, 0.85);
}

.pageDesc {
  font-size: 14px;
  color: #6c757d;
  margin-top: 0;
  margin-left: 16px;
  display: block;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.content-section {
  padding: 0;
}

.content-wrap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  padding: 0;
}

/* 탭 스타일 */
:deep(.ant-tabs) {
  background: white;
  border-radius: 8px;
}

:deep(.ant-tabs-nav) {
  margin-bottom: 0;
  padding: 0 24px;
}

:deep(.ant-tabs-tab) {
  padding: 12px 20px;
  font-size: 15px;
}

:deep(.ant-tabs-content) {
  padding: 20px;
}

/* 준비중 콘텐츠 */
.preparing-content {
  padding: 60px 0;
  text-align: center;
}
</style>